
import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Save,
  Link,
  Search,
  Filter,
  AlertCircle,
  CheckCircle2,
  Target
} from 'lucide-react';
import { authenticatedFetch } from '../services/auth';
import { API_BASE } from '../utils/apiConfig';

const ObjectiveLinkManagement = ({ onClose }) => {
  const [objectiveLinks, setObjectiveLinks] = useState([]);
  const [perspectives, setPerspectives] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [perspectiveObjectives, setPerspectiveObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPerspective, setFilterPerspective] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    perspective_id: '',
    objective_id: '',
    objective_id_lig: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.perspective_id && objectives.length > 0) {
      console.log('🔄 Refiltrando objetivos - perspectiva:', formData.perspective_id, 'objetivos disponíveis:', objectives.length);
      loadObjectivesByPerspective(formData.perspective_id);
    } else {
      console.log('🧹 Limpando objetivos da perspectiva');
      setPerspectiveObjectives([]);
    }
  }, [formData.perspective_id, objectives]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      console.log('Carregando dados iniciais...');

      await Promise.all([
        loadPerspectives(),
        loadAllObjectives(),
        loadObjectiveLinks()
      ]);

      setError(null);
    } catch (err) {
      console.error('Erro ao carregar dados iniciais:', err);
      setError('Erro ao carregar dados iniciais: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPerspectives = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/perspectives`);
      if (response.ok) {
        const data = await response.json();
        setPerspectives(data || []);
        console.log('✅ Perspectivas carregadas:', data);
      } else {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
    } catch (err) {
      console.error('Erro ao carregar perspectivas:', err);
      setError('Erro ao carregar perspectivas: ' + err.message);
      setPerspectives([]);
    }
  };

  const loadAllObjectives = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/bsc-objectives`);
      if (response.ok) {
        const data = await response.json();
        setObjectives(data || []);
        console.log('✅ Objetivos carregados:', data);
      } else {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
    } catch (err) {
      console.error('Erro ao carregar objetivos:', err);
      setError('Erro ao carregar objetivos: ' + err.message);
      setObjectives([]);
    }
  };

  const loadObjectiveLinks = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/objective-links`);
      if (response.ok) {
        const data = await response.json();
        setObjectiveLinks(data || []);
        console.log('✅ Vínculos carregados:', data);
      } else {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
    } catch (err) {
      console.error('Erro ao carregar vínculos:', err);
      setError('Erro ao carregar vínculos: ' + err.message);
      setObjectiveLinks([]);
    }
  };

  const loadObjectivesByPerspective = (perspectiveId) => {
    console.log('🔍 Filtrando objetivos para perspectiva:', perspectiveId, typeof perspectiveId);
    console.log('📊 Objetivos disponíveis:', objectives);

    if (!perspectiveId || perspectiveId === '') {
      console.log('❌ Perspectiva ID vazio, limpando objetivos');
      setPerspectiveObjectives([]);
      return;
    }

    const targetPerspectiveId = parseInt(perspectiveId);
    console.log('🎯 ID da perspectiva alvo:', targetPerspectiveId);

    const filtered = objectives.filter(obj => {
      const objPerspectiveId = parseInt(obj.perspective_id);
      console.log(`Comparando objetivo "${obj.name}":`, {
        objective_perspective_id: obj.perspective_id,
        parsed_objective_perspective_id: objPerspectiveId,
        target_perspective_id: targetPerspectiveId,
        match: objPerspectiveId === targetPerspectiveId
      });
      return objPerspectiveId === targetPerspectiveId;
    });

    console.log('✅ Objetivos filtrados para perspectiva', targetPerspectiveId, ':', filtered);
    setPerspectiveObjectives(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.perspective_id || !formData.objective_id || !formData.objective_id_lig) {
      setError('Todos os campos são obrigatórios');
      return;
    }

    if (formData.objective_id === formData.objective_id_lig) {
      setError('O objetivo principal não pode ser vinculado a ele mesmo');
      return;
    }

    try {
      const url = editingLink
        ? `${API_BASE}/api/objective-links/${editingLink.id}`
        : `${API_BASE}/api/objective-links`;

      const method = editingLink ? 'PUT' : 'POST';

      const payload = {
        perspective_id: parseInt(formData.perspective_id),
        objective_id: formData.objective_id,
        objective_id_lig: formData.objective_id_lig
      };

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      setSuccess(editingLink ? 'Vínculo atualizado com sucesso!' : 'Vínculo criado com sucesso!');
      await loadObjectiveLinks();
      setShowModal(false);
      setEditingLink(null);
      setFormData({
        perspective_id: '',
        objective_id: '',
        objective_id_lig: ''
      });

      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao salvar vínculo:', err);
      setError('Erro ao salvar vínculo: ' + err.message);
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      perspective_id: link.perspective_id?.toString() || '',
      objective_id: link.objective_id,
      objective_id_lig: link.objective_id_lig
    });
    setShowModal(true);
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Tem certeza que deseja excluir este vínculo?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_BASE}/api/objective-links/${linkId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      setSuccess('Vínculo excluído com sucesso!');
      await loadObjectiveLinks();

      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao deletar vínculo:', err);
      setError('Erro ao deletar vínculo: ' + err.message);
    }
  };

  const getObjectiveName = (objectiveId) => {
    const objective = objectives.find(obj => obj.objective_id?.toString() === objectiveId?.toString());
    return objective ? objective.name : 'Objetivo não encontrado';
  };

  const getPerspectiveName = (perspectiveId) => {
    const perspective = perspectives.find(p => p.perspective_id === perspectiveId);
    return perspective ? perspective.name : 'Perspectiva não encontrada';
  };

  const filteredLinks = objectiveLinks.filter(link => {
    const objectiveName = getObjectiveName(link.objective_id);
    const linkedObjectiveName = getObjectiveName(link.objective_id_lig);
    const perspectiveName = getPerspectiveName(link.perspective_id);

    const matchesSearch = objectiveName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      linkedObjectiveName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      perspectiveName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPerspective = filterPerspective === 'all' ||
      link.perspective_id === parseInt(filterPerspective);

    return matchesSearch && matchesPerspective;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="h-full">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Link className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Gestão de Vínculos entre Objetivos</h1>
                  <p className="text-gray-600 text-sm">Configurar relacionamentos entre objetivos estratégicos</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="w-full mx-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-green-700">{success}</span>
              <button
                onClick={() => setSuccess('')}
                className="ml-auto text-green-700 hover:text-green-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar vínculos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterPerspective}
                  onChange={(e) => setFilterPerspective(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="all">Todas as Perspectivas</option>
                  {perspectives.map((perspective) => (
                    <option key={perspective.perspective_id} value={perspective.perspective_id}>
                      {perspective.name}
                    </option>
                  ))}
                </select>
                <div className="ml-4 border-l border-gray-300 pl-4">
                  <button
                    onClick={() => {
                      setEditingLink(null);
                      setFormData({
                        perspective_id: '',
                        objective_id: '',
                        objective_id_lig: ''
                      });
                      setShowModal(true);
                    }}
                    className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg text-white font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Novo Vínculo
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Vínculos */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando vínculos...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Perspectiva
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Objetivo Principal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Objetivo Impactado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLinks.map((link) => (
                      <tr key={link.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                            {getPerspectiveName(link.perspective_id)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <Target className="w-4 h-4 text-blue-500 mr-2" />
                            {getObjectiveName(link.objective_id)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <Target className="w-4 h-4 text-purple-500 mr-2" />
                            {getObjectiveName(link.objective_id_lig)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(link)}
                              className="text-cyan-600 hover:text-cyan-900 p-1 rounded"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(link.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredLinks.length === 0 && (
                  <div className="p-12 text-center">
                    <Link className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Nenhum vínculo encontrado</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Edição/Criação */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingLink ? 'Editar Vínculo' : 'Novo Vínculo'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Perspectiva
                </label>
                <select
                  value={formData.perspective_id}
                  onChange={(e) => {
                    console.log('🔄 Perspectiva selecionada:', e.target.value);
                    setFormData({ ...formData, perspective_id: e.target.value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  required
                >
                  <option value="">Selecione uma perspectiva</option>
                  {perspectives.map((perspective) => (
                    <option key={perspective.perspective_id} value={perspective.perspective_id}>
                      {perspective.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objetivo Principal
                </label>
                <select
                  value={formData.objective_id}
                  onChange={(e) => setFormData({ ...formData, objective_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  required
                  disabled={!formData.perspective_id}
                >
                  <option value="">Selecione um objetivo</option>
                  {perspectiveObjectives.map((objective) => (
                    <option key={objective.id} value={objective.objective_id}>
                      {objective.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objetivo Impactado
                </label>
                <select
                  value={formData.objective_id_lig}
                  onChange={(e) => setFormData({ ...formData, objective_id_lig: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  required
                >
                  <option value="">Selecione o objetivo impactado</option>
                  {objectives.map((objective) => (
                    <option key={objective.id} value={objective.objective_id}>
                      {objective.name} ({getPerspectiveName(objective.perspective_id)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObjectiveLinkManagement;
